import React from 'react';
import ReactTooltip from 'react-tooltip';
import styled from 'styled-components';
import { getExpiredBalances } from '~/javascripts/admin/actions/transactions';
import { formatDate, formatNum, formatUUID } from '~/javascripts/admin/utils';
import { createBadge } from '../../atoms/Badge';
import Table from '../../atoms/Table';
import SeqPager from '../../organisms/SeqPager';

type Props = {
  balance?: Awaited<ReturnType<typeof getExpiredBalances>>;
  onChangePage: (paginatedLink: string | null) => void;
};

type Header = {
  label: string;
  size: number;
  align?: 'center' | 'justify' | 'left' | 'right';
  tooltipKey?: string;
  tooltipText?: string | React.ReactNode;
};

const headers: Header[] = [
  { label: '種別', size: 1 },
  {
    label: '日時',
    size: 1,
    tooltipKey: 'expiresAt',
    tooltipText: (
      <>
        以下では有効期限の日付表示が異なります
        <br />
        お客様詳細&gt;残高詳細: 失効日時が00:00の場合のみ前日を表示
        <br />
        Pokepayアプリ: 常に前日を表示
      </>
    ),
  },
  { label: 'お客様ID', size: 1.5 },
  { label: 'マネー', size: 1.25, align: 'right' },
  { label: 'ポイント', size: 1.25, align: 'right' },
  { label: '', size: 6 }, // table design uses only half of the table
];

const TooltipTableCell = styled(Table.Cell)<{ tooltip?: boolean }>`
  &:hover {
    ${({ tooltip }) => tooltip && 'cursor: pointer; background-color: #eeeeee;'}
  }
`;

const ExpiredBalancesBadge = createBadge({
  expire: { label: '失効', bgColor: '#000000' },
  cashback: {
    label: '返金',
    bgColor: '#000000',
  },
  account_limited_balance_expire: {
    label: '期限切れ',
    bgColor: '#C90E27',
  },
});

const ListTable = ({ balance, onChangePage }: Props) => {
  return (
    <Table>
      <Table.Header>
        <Table.Row>
          {headers.map((c) => (
            <TooltipTableCell
              size={c.size}
              key={c.label}
              align={c.align}
              tooltip={!!c.tooltipKey && !!c.tooltipText}
              data-tip
              data-for={c.tooltipKey}
            >
              {c.label}
              {!!c.tooltipKey && !!c.tooltipText && (
                <ReactTooltip id={c.tooltipKey} type="dark" place="top" effect="solid">
                  <span>{c.tooltipText}</span>
                </ReactTooltip>
              )}
            </TooltipTableCell>
          ))}
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {balance?.rows?.map((b) => (
          <Table.Row key={b.id}>
            <Table.Cell wordBreak="keep-all">
              <ExpiredBalancesBadge
                status={b.type === null ? 'account_limited_balance_expire' : b.type}
              />
            </Table.Cell>
            <Table.Cell wordBreak="keep-all">{formatDate(b.expires_at)}</Table.Cell>
            <Table.Cell wordBreak="keep-all">{formatUUID(b.customer_id)}</Table.Cell>
            <Table.Cell wordBreak="break-word" align="right">
              {formatNum(b.money_amount)}
            </Table.Cell>
            <Table.Cell wordBreak="break-word" align="right">
              {formatNum(b.point_amount)}
            </Table.Cell>
            <Table.Cell />
          </Table.Row>
        ))}
      </Table.Body>
      <Table.Footer>
        <Table.Row noBorder>
          <Table.Cell colSpan={6} align="center" styles={{ paddingTop: '16px' }}>
            {balance && (
              <SeqPager prev={balance.prev} next={balance.next} onChangePage={onChangePage} />
            )}
          </Table.Cell>
        </Table.Row>
      </Table.Footer>
    </Table>
  );
};

export default ListTable;
